import type { FeatureConfig } from '~/lib/builder-types';

// ── Stack Configuration ────────────────────────────────────────────────────

export type FrontendChoice =
  | 'nextjs'
  | 'react-spa'
  | 'remix'
  | 'vue'
  | 'sveltekit'
  | 'tanstack-start'
  | 'tanstack-router';

export type BackendChoice =
  | 'node-express'
  | 'node-fastify'
  | 'python-fastapi'
  | 'rails'
  | 'go';

export type DatabaseChoice = 'postgres' | 'mysql' | 'sqlite' | 'mongodb';

export type AuthProviderChoice =
  | 'better-auth'
  | 'auth0-clerk'
  | 'passport'
  | 'devise';

export type RealtimeChoice =
  | 'websockets'
  | 'sse'
  | 'polling'
  | 'pusher-ably'
  | 'supabase-realtime';

export type HostingChoice = 'vercel' | 'fly-io' | 'railway' | 'aws' | 'render';

export interface TechStackConfig {
  frontend: FrontendChoice;
  backend: BackendChoice;
  database: DatabaseChoice;
  authProvider: AuthProviderChoice;
  realtime: RealtimeChoice;
  hosting: HostingChoice;
}

export interface TechStackOption {
  id: string;
  label: string;
  description: string;
}

export interface TechStackCategory {
  id: keyof TechStackConfig;
  label: string;
  options: TechStackOption[];
}

// ── Stack Presets ──────────────────────────────────────────────────────────

export interface StackPreset {
  id: string;
  name: string;
  description: string;
  stack: TechStackConfig;
  badges: string[];
}

// ── Generated Technical Spec Items ────────────────────────────────────────

export interface PageRoute {
  id: string;
  path: string;
  label: string;
  description: string;
  authRequired: boolean;
}

export interface UXFlow {
  id: string;
  title: string;
  steps: string[];
}

export interface ApiEndpoint {
  id: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  path: string;
  description: string;
  requestBody?: string;
  response?: string;
}

export interface DbColumn {
  name: string;
  type: string;
  nullable: boolean;
  notes?: string;
}

export interface DbTable {
  id: string;
  name: string;
  columns: DbColumn[];
  notes?: string;
}

export interface FeatureTechSpec {
  pages: PageRoute[];
  uxFlows: UXFlow[];
  endpoints: ApiEndpoint[];
  dbTables: DbTable[];
}

// ── Resolver Pattern ───────────────────────────────────────────────────────

export type TechSpecResolver = (
  stack: TechStackConfig,
  featureConfig?: FeatureConfig,
) => FeatureTechSpec;

export interface FeatureTechSpecEntry {
  featureSlug: string;
  resolve: TechSpecResolver;
}

// ── Customization ─────────────────────────────────────────────────────────

export interface FeatureTechSpecDelta {
  pages?: { added: PageRoute[]; removedIds: string[] };
  endpoints?: { added: ApiEndpoint[]; removedIds: string[] };
  dbTables?: { added: DbTable[]; removedIds: string[] };
  uxFlows?: { added: UXFlow[]; removedIds: string[] };
}

export interface TechSpecCustomizations {
  [featureSlug: string]: FeatureTechSpecDelta;
}

// ── Full State (URL-serialised) ───────────────────────────────────────────

export interface TechSpecState {
  stack: TechStackConfig;
  customizations: TechSpecCustomizations;
}

// ── Stack category definitions ────────────────────────────────────────────

export const STACK_CATEGORIES: TechStackCategory[] = [
  {
    id: 'frontend',
    label: 'Frontend',
    options: [
      {
        id: 'nextjs',
        label: 'Next.js',
        description: 'React framework with SSR/SSG',
      },
      {
        id: 'react-spa',
        label: 'React SPA',
        description: 'Client-side React with Vite',
      },
      {
        id: 'remix',
        label: 'Remix / React Router',
        description: 'Full-stack React, edge-ready',
      },
      {
        id: 'vue',
        label: 'Vue 3 + Nuxt',
        description: 'Vue framework with SSR',
      },
      {
        id: 'sveltekit',
        label: 'SvelteKit',
        description: 'Svelte full-stack framework',
      },
      {
        id: 'tanstack-start',
        label: 'TanStack Start',
        description: 'Full-stack React with SSR & server functions',
      },
      {
        id: 'tanstack-router',
        label: 'TanStack Router',
        description: 'Type-safe client-side React routing',
      },
    ],
  },
  {
    id: 'backend',
    label: 'Backend',
    options: [
      {
        id: 'node-express',
        label: 'Node + Express',
        description: 'Classic Node.js HTTP server',
      },
      {
        id: 'node-fastify',
        label: 'Node + Fastify',
        description: 'Fast Node.js with TypeScript',
      },
      {
        id: 'python-fastapi',
        label: 'Python + FastAPI',
        description: 'Async Python REST API',
      },
      {
        id: 'rails',
        label: 'Ruby on Rails',
        description: 'Convention-over-config full stack',
      },
      {
        id: 'go',
        label: 'Go (net/http)',
        description: 'Compiled, low-latency services',
      },
    ],
  },
  {
    id: 'database',
    label: 'Database',
    options: [
      {
        id: 'postgres',
        label: 'PostgreSQL',
        description: 'Relational — best default choice',
      },
      {
        id: 'mysql',
        label: 'MySQL',
        description: 'Relational — common in legacy stacks',
      },
      {
        id: 'sqlite',
        label: 'SQLite / Turso',
        description: 'Embedded, edge-friendly',
      },
      {
        id: 'mongodb',
        label: 'MongoDB',
        description: 'Document store, flexible schema',
      },
    ],
  },
  {
    id: 'authProvider',
    label: 'Auth',
    options: [
      {
        id: 'better-auth',
        label: 'better-auth / Auth.js',
        description: 'Open-source, self-hosted',
      },
      {
        id: 'auth0-clerk',
        label: 'Auth0 / Clerk',
        description: 'Managed auth-as-a-service',
      },
      {
        id: 'passport',
        label: 'Passport.js',
        description: 'Node middleware, flexible strategies',
      },
      {
        id: 'devise',
        label: 'Devise',
        description: 'Rails authentication gem',
      },
    ],
  },
  {
    id: 'realtime',
    label: 'Realtime',
    options: [
      {
        id: 'websockets',
        label: 'WebSockets',
        description: 'Native bidirectional, low latency',
      },
      {
        id: 'sse',
        label: 'Server-Sent Events',
        description: 'One-way streaming over HTTP',
      },
      {
        id: 'polling',
        label: 'HTTP Polling',
        description: 'Simple, works everywhere',
      },
      {
        id: 'pusher-ably',
        label: 'Pusher / Ably',
        description: 'Managed pub/sub service',
      },
      {
        id: 'supabase-realtime',
        label: 'Supabase Realtime',
        description: 'DB change streaming',
      },
    ],
  },
  {
    id: 'hosting',
    label: 'Hosting',
    options: [
      {
        id: 'vercel',
        label: 'Vercel',
        description: 'Edge + serverless, great for Next.js',
      },
      {
        id: 'fly-io',
        label: 'Fly.io',
        description: 'Global VMs, great for persistent services',
      },
      {
        id: 'railway',
        label: 'Railway',
        description: 'Simple PaaS with Postgres included',
      },
      { id: 'aws', label: 'AWS', description: 'Full control, many services' },
      {
        id: 'render',
        label: 'Render',
        description: 'Simple PaaS, good free tier',
      },
    ],
  },
];
